// ============================================================================
// useNotificationHub
// ============================================================================
// Server-backed in-app inbox for the header bell. Polls the unread count on an
// interval and loads the list on demand (when the panel opens or on refresh).
// All calls go through prima-api's inbox proxy.

import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationHubService } from 'api/services/notificationHubService';
import type { InAppNotification } from 'api/types';

const DEFAULT_POLL_MS = 60_000;
const DEFAULT_PAGE_SIZE = 50;

export interface UseNotificationHubOptions {
  /** Poll interval for the unread count. 0 disables polling. */
  pollMs?: number;
  pageSize?: number;
  /** Set false to disable all calls (e.g. before login). */
  enabled?: boolean;
}

export const useNotificationHub = (options: UseNotificationHubOptions = {}) => {
  const { pollMs = DEFAULT_POLL_MS, pageSize = DEFAULT_PAGE_SIZE, enabled = true } = options;

  const [items, setItems] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const count = await notificationHubService.getUnreadCount();
      if (mounted.current) setUnreadCount(count);
    } catch (err) {
      // Unread count is a convenience; never surface polling failures to the user.
      if (mounted.current) setError(err as Error);
    }
  }, [enabled]);

  const loadInbox = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const page = await notificationHubService.getInbox(1, pageSize, false);
      if (mounted.current) {
        setItems(page.items);
        setUnreadCount(page.items.filter(i => !i.isRead).length);
      }
    } catch (err) {
      if (mounted.current) setError(err as Error);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [enabled, pageSize]);

  const markRead = useCallback(async (channelId: string) => {
    // Optimistic update, then sync with the hub.
    setItems(prev => prev.map(i => (i.channelId === channelId ? { ...i, isRead: true } : i)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await notificationHubService.markRead({ channelIds: [channelId] });
    } catch (err) {
      setError(err as Error);
      await loadInbox();
    }
  }, [loadInbox]);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(i => ({ ...i, isRead: true })));
    setUnreadCount(0);
    try {
      await notificationHubService.markAllRead();
    } catch (err) {
      setError(err as Error);
      await loadInbox();
    }
  }, [loadInbox]);

  const archive = useCallback(async (channelId: string) => {
    const wasUnread = items.find(i => i.channelId === channelId)?.isRead === false;
    setItems(prev => prev.filter(i => i.channelId !== channelId));
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await notificationHubService.archive(channelId);
    } catch (err) {
      setError(err as Error);
      await loadInbox();
    }
  }, [items, loadInbox]);

  // Poll the unread count.
  useEffect(() => {
    if (!enabled) return;
    refreshUnreadCount();
    if (pollMs <= 0) return;
    const timer = window.setInterval(refreshUnreadCount, pollMs);
    return () => window.clearInterval(timer);
  }, [enabled, pollMs, refreshUnreadCount]);

  return {
    items,
    unreadCount,
    loading,
    error,
    loadInbox,
    refreshUnreadCount,
    markRead,
    markAllRead,
    archive,
  };
};

export default useNotificationHub;
